/**
 * Conceited Utilities — shared module for the Conceited Shopify theme.
 * Consolidates: flattenVariants, renderCard, collectionGrid, Cart, Carousel, toast.
 * Load once in theme.liquid before other scripts.
 */
window.Conceited = (function() {
  'use strict';

  /* ══════════════════════════════════════════════
   * TOAST
   * ══════════════════════════════════════════════ */

  function toast(message, type, duration) {
    type = type || 'success';
    duration = duration || 3000;
    var t = document.createElement('div');
    t.className = 'fixed z-[200] px-6 py-3 text-sm font-medium shadow-lg transition-all duration-300 ' +
      (type === 'error' ? 'bg-red-600 text-white bottom-4 right-4' : 'bg-foreground text-background bottom-4 right-4');
    t.textContent = message;
    t.style.opacity = '0';
    t.style.transform = 'translateY(10px)';
    document.body.appendChild(t);
    requestAnimationFrame(function() {
      t.style.opacity = '1';
      t.style.transform = 'translateY(0)';
    });
    setTimeout(function() {
      t.style.opacity = '0';
      t.style.transform = 'translateY(10px)';
      setTimeout(function() { t.remove(); }, 300);
    }, duration);
  }

  /* ══════════════════════════════════════════════
   * CART SERVICE
   * ══════════════════════════════════════════════ */

  function updateCartBadges() {
    fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
      applyCartCount(cart.item_count);
    }).catch(function() {});
  }

  function applyCartCount(count) {
    var display = count > 99 ? '99+' : String(count);
    document.querySelectorAll('[data-cart-count]').forEach(function(el) {
      el.textContent = display;
      el.classList.toggle('hidden', count === 0);
    });
    var badges = document.querySelectorAll('.cart-count-badge');
    badges.forEach(function(el) {
      el.textContent = display;
      el.classList.toggle('hidden', count === 0);
    });
  }

  // Listen for cart:updated events from cart drawer
  window.addEventListener('cart:updated', function(e) {
    if (e.detail && typeof e.detail.item_count === 'number') {
      applyCartCount(e.detail.item_count);
    } else {
      updateCartBadges();
    }
  });

  var Cart = {
    add: function(variantId, qty, properties) {
      qty = qty || 1;
      var item = { id: variantId, quantity: qty };
      if (properties) item.properties = properties;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      }).then(function(r) {
        if (!r.ok) throw new Error('Cart add failed');
        return r.json();
      }).then(function(data) {
        updateCartBadges();
        return data;
      }).catch(function(err) {
        toast('Error adding to cart', 'error');
        throw err;
      });
    },

    change: function(lineKey, qty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      }).then(function(r) {
        if (!r.ok) throw new Error('Cart change failed');
        return r.json();
      }).then(function(data) {
        updateCartBadges();
        return data;
      }).catch(function(err) {
        toast('Error updating cart', 'error');
        throw err;
      });
    },

    remove: function(lineKey) {
      return Cart.change(lineKey, 0);
    },

    fetch: function() {
      return fetch('/cart.js').then(function(r) { return r.json(); });
    }
  };

  /* ══════════════════════════════════════════════
   * FLATTEN VARIANTS
   * ══════════════════════════════════════════════ */

  function flattenVariants(products, opts) {
    opts = opts || {};
    var colorOption = (opts.colorOption || 'color').toLowerCase();
    var cards = [];
    var seen = {};

    products.forEach(function(product) {
      // Find color option index
      var colorIdx = -1;
      product.options.forEach(function(opt, i) {
        var name = (typeof opt === 'string') ? opt : (opt.name || '');
        if (name.toLowerCase() === colorOption) colorIdx = i;
      });

      if (colorIdx === -1) {
        // No color option — one card per product
        var first = product.variants.find(function(v) { return v.available; }) || product.variants[0];
        if (first) {
          cards.push(buildCard(product, first, '', colorIdx));
        }
        return;
      }

      // Group variants by color
      var groups = {};
      product.variants.forEach(function(v) {
        var color = v['option' + (colorIdx + 1)];
        if (!color) return;
        if (!groups[color]) groups[color] = [];
        groups[color].push(v);
      });

      Object.keys(groups).forEach(function(color) {
        var variants = groups[color];
        var hasAvailable = variants.some(function(v) { return v.available; });
        if (!hasAvailable && !opts.includeSoldOut) return;

        var key = product.handle + '-' + color;
        if (seen[key]) return;
        seen[key] = true;

        var first = variants.find(function(v) { return v.available; }) || variants[0];
        cards.push(buildCard(product, first, color, colorIdx));
      });
    });

    return cards;
  }

  function getImageSrc(img) {
    return (typeof img === 'string') ? img : (img.src || '');
  }

  function buildCard(product, variant, color, colorIdx) {
    // Resolve image: variant featured_image → image_id match → alt/filename match → product fallback
    var image = '';

    // 1. Direct variant featured_image
    if (variant.featured_image) {
      image = variant.featured_image.src || getImageSrc(variant.featured_image);
    }

    // 2. Match via image.variant_ids[] or variant.image_id
    if (!image) {
      for (var i = 0; i < product.images.length; i++) {
        var img = product.images[i];
        // Check if this image's variant_ids includes our variant
        if (img.variant_ids && img.variant_ids.indexOf(variant.id) !== -1) {
          image = getImageSrc(img);
          break;
        }
        // Fallback: match by image_id
        if (!image && variant.image_id && img.id && img.id === variant.image_id) {
          image = getImageSrc(img);
          break;
        }
      }
    }

    // 3. Match by color name in alt text or filename
    if (!image && color) {
      var colorLower = color.toLowerCase();
      for (var i = 0; i < product.images.length; i++) {
        var img = product.images[i];
        var src = getImageSrc(img);
        var alt = (typeof img === 'string') ? '' : (img.alt || '');
        if ((src.toLowerCase().indexOf(colorLower) !== -1) || (alt.toLowerCase().indexOf(colorLower) !== -1)) {
          image = src;
          break;
        }
      }
    }

    // 4. Fallback to first product image
    if (!image && product.images.length > 0) {
      image = getImageSrc(product.images[0]);
    }

    var price = variant.price;
    var compareAtPrice = variant.compare_at_price && variant.compare_at_price > price ? variant.compare_at_price : null;

    return {
      handle: product.handle,
      title: product.title,
      colorName: color,
      price: price,
      compareAtPrice: compareAtPrice,
      image: image,
      variantId: variant.id,
      available: variant.available,
      productType: product.product_type,
      url: color
        ? '/products/' + product.handle + '?color=' + encodeURIComponent(color)
        : '/products/' + product.handle
    };
  }

  /* ══════════════════════════════════════════════
   * RENDER CARD
   * ══════════════════════════════════════════════ */

  function renderCard(card, opts) {
    opts = opts || {};
    var showWishlist = opts.showWishlist !== false;
    var showColor = opts.showColor !== false;
    var displayName = opts.displayNameFormat === 'pipe' && card.colorName
      ? card.title + ' | ' + card.colorName
      : card.title;

    var priceStr = formatPrice(card.price);
    var compareStr = card.compareAtPrice ? formatPrice(card.compareAtPrice) : '';

    var imgSrc = card.image || '';

    var wishlistHtml = '';
    if (showWishlist) {
      var wlData = JSON.stringify({
        variantId: String(card.variantId),
        productHandle: card.handle || '',
        productTitle: card.title || '',
        colorName: card.colorName || '',
        imageUrl: card.image || '',
        price: String(card.price || '0'),
        currencyCode: 'USD'
      }).replace(/'/g, '&#39;').replace(/"/g, '&quot;');

      wishlistHtml = '<button type="button" data-wishlist-toggle data-variant-id="' + card.variantId + '" ' +
        'data-product-data="' + wlData + '" ' +
        'aria-label="Add to wishlist" ' +
        'class="absolute top-3 right-3 z-10 flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background/80 backdrop-blur-sm shadow-sm transition-transform duration-200 hover:scale-110">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="wishlist-heart w-5 h-5 transition-colors duration-200">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
        '</svg></button>';
    }

    var priceHtml = '';
    if (compareStr) {
      priceHtml = '<span class="text-sm line-through text-muted-foreground mr-2">' + compareStr + '</span>' +
        '<span class="text-product-price text-red-600">' + priceStr + '</span>';
    } else {
      priceHtml = '<p class="text-product-price flex-shrink-0">' + priceStr + '</p>';
    }

    var colorHtml = '';
    if (showColor && card.colorName) {
      colorHtml = '<p class="text-sm text-muted-foreground truncate">' + card.colorName + '</p>';
    }

    return '<div class="group w-full">' +
      '<a href="' + card.url + '" class="block">' +
        '<div class="relative h-[350px] md:h-[450px] mb-4 w-full overflow-hidden bg-muted">' +
          wishlistHtml +
          (imgSrc
            ? '<img src="' + imgSrc + '" alt="' + escapeHtml(displayName) + '" width="600" height="800" loading="lazy" decoding="async" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />'
            : '<div class="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-sm">No image</div>') +
        '</div>' +
        '<div class="flex flex-col md:flex-row md:items-center md:justify-between md:gap-6 lg:gap-8">' +
          '<div class="flex flex-col flex-1 min-w-0">' +
            '<p class="text-product-name truncate">' + escapeHtml(displayName) + '</p>' +
            colorHtml +
          '</div>' +
          priceHtml +
        '</div>' +
      '</a>' +
    '</div>';
  }

  function formatPrice(price) {
    var num = parseFloat(price);
    if (isNaN(num)) return '$0.00';
    // Shopify REST API returns prices as dollar strings (e.g. "50.00"), not cents
    return '$' + num.toFixed(2);
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ══════════════════════════════════════════════
   * COLLECTION GRID (the 80% function)
   * ══════════════════════════════════════════════ */

  function getResponsivePageSize() {
    var w = window.innerWidth;
    if (w < 768) return 6;       // 2 cols × 3 rows
    if (w < 1024) return 6;      // 3 cols × 2 rows
    return 8;                     // 4 cols × 2 rows
  }

  function collectionGrid(containerEl, collectionHandle, opts) {
    opts = opts || {};
    var responsive = opts.responsive !== false;
    var perPage = responsive ? getResponsivePageSize() : (opts.perPage || 8);
    var filter = opts.filter || null;
    var sort = opts.sort || null;
    var colorOption = opts.colorOption || 'color';
    var loadMoreText = opts.loadMoreText || 'Load More';
    var emptyText = opts.emptyText || 'No products found.';
    var cardOpts = opts.cardOpts || {};
    var onLoaded = opts.onLoaded || null;
    var onRendered = opts.onRendered || null;

    // Filter config: array of { label, keyword, matchType? } objects
    var filterConfig = opts.filterConfig || [];
    var filterMode = opts.filterMode || 'none'; // 'title' | 'productType' | 'none'

    var allCards = [];
    var filteredCards = [];
    var activeFilter = 'all';
    var visibleCount = 0;
    var loadMoreBtn = null;
    var filterContainer = opts.filterContainer || null;
    var emptyEl = opts.emptyContainer || null;

    // Loading skeleton
    containerEl.innerHTML = buildSkeleton(perPage);

    // Fetch products
    fetchAllProducts(collectionHandle, function(products) {
      containerEl.innerHTML = '';

      allCards = flattenVariants(products, { colorOption: colorOption, includeSoldOut: opts.includeSoldOut });

      if (filter) allCards = allCards.filter(filter);
      if (sort) allCards.sort(sort);

      // Build filter UI if config provided
      if (filterConfig.length > 0 && filterContainer) {
        buildFilterUI();
      }

      applyFilter('all');

      if (onRendered) onRendered(allCards);
      if (onLoaded) onLoaded(allCards);
    });

    function applyFilterToCards(filterKey) {
      if (filterKey === 'all') return allCards.slice();
      return allCards.filter(function(card) {
        if (filterMode === 'title') {
          return (card.title || '').toLowerCase().indexOf(filterKey.toLowerCase()) !== -1;
        } else if (filterMode === 'productType') {
          return (card.productType || '') === filterKey;
        }
        return true;
      });
    }

    function applyFilter(filterKey) {
      activeFilter = filterKey;
      filteredCards = applyFilterToCards(filterKey);
      visibleCount = responsive ? getResponsivePageSize() : perPage;
      render();
      updateFilterButtons();
    }

    function render() {
      if (filteredCards.length === 0) {
        containerEl.innerHTML = '<p class="text-center text-foreground/50 py-16 col-span-full flex items-center justify-center text-medium">' + emptyText + '</p>';
        if (emptyEl) emptyEl.classList.remove('hidden');
        if (loadMoreBtn) { loadMoreBtn.remove(); loadMoreBtn = null; }
        return;
      }
      if (emptyEl) emptyEl.classList.add('hidden');

      var html = '';
      var end = Math.min(visibleCount, filteredCards.length);
      for (var i = 0; i < end; i++) {
        html += '<div data-product-card data-product-type="' + (filteredCards[i].productType || '').replace(/"/g, '&quot;') + '" data-product-title="' + (filteredCards[i].title || '').toLowerCase().replace(/"/g, '&quot;') + '">' + renderCard(filteredCards[i], cardOpts) + '</div>';
      }
      containerEl.innerHTML = html;

      // Load More button
      if (loadMoreBtn) { loadMoreBtn.remove(); loadMoreBtn = null; }
      if (visibleCount < filteredCards.length) {
        loadMoreBtn = document.createElement('div');
        loadMoreBtn.className = 'flex justify-center mt-12 col-span-full';
        loadMoreBtn.innerHTML = '<button type="button" class="inline-flex items-center justify-center px-8 py-3 border border-foreground text-sm font-medium transition-opacity hover:opacity-70">' + loadMoreText + '</button>';
        loadMoreBtn.querySelector('button').addEventListener('click', function() {
          var increment = responsive ? getResponsivePageSize() : perPage;
          visibleCount += increment;
          render();
        });
        containerEl.parentNode.insertBefore(loadMoreBtn, containerEl.nextSibling);
      }

      // Refresh wishlist hearts
      if (window.WishlistManager && typeof window.WishlistManager.refreshHearts === 'function') {
        window.WishlistManager.refreshHearts();
      }
    }

    function buildFilterUI() {
      var flex = filterContainer.querySelector('.flex');
      if (!flex) return;
      var html = '<button class="everything-filter-btn whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors border-b-2 border-foreground text-foreground" data-filter="all">All</button>';
      filterConfig.forEach(function(f) {
        html += '<button class="everything-filter-btn whitespace-nowrap px-4 py-2 text-sm transition-colors text-muted-foreground hover:text-foreground border-b-2 border-transparent" data-filter="' + f.keyword + '">' + f.label + '</button>';
      });
      flex.innerHTML = html;
      filterContainer.classList.remove('hidden');

      // Attach click handlers
      flex.querySelectorAll('.everything-filter-btn').forEach(function(btn) {
        btn.addEventListener('click', function() {
          applyFilter(btn.getAttribute('data-filter'));
        });
      });
    }

    function updateFilterButtons() {
      if (!filterContainer) return;
      filterContainer.querySelectorAll('.everything-filter-btn').forEach(function(btn) {
        var isActive = btn.getAttribute('data-filter') === activeFilter;
        if (isActive) {
          btn.classList.add('border-foreground', 'text-foreground', 'font-medium');
          btn.classList.remove('border-transparent', 'text-muted-foreground');
        } else {
          btn.classList.remove('border-foreground', 'text-foreground', 'font-medium');
          btn.classList.add('border-transparent', 'text-muted-foreground');
        }
      });
    }

    return {
      getCards: function() { return allCards; },
      getFilteredCards: function() { return filteredCards; },
      applyFilter: applyFilter,
      refresh: function() { render(); }
    };
  }

  function fetchAllProducts(handle, callback, page, accumulated) {
    page = page || 1;
    accumulated = accumulated || [];
    fetch('/collections/' + handle + '/products.json?limit=250&page=' + page)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var products = data.products || [];
        accumulated = accumulated.concat(products);
        if (products.length === 250) {
          fetchAllProducts(handle, callback, page + 1, accumulated);
        } else {
          // Enrich with full product data for proper variant images
          enrichProducts(accumulated, function(enriched) {
            callback(enriched);
          });
        }
      })
      .catch(function() { callback(accumulated); });
  }

  function enrichProducts(products, callback) {
    var remaining = products.length;
    if (remaining === 0) { callback(products); return; }

    products.forEach(function(product, idx) {
      fetch('/products/' + product.handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.product) {
            products[idx] = data.product;
          }
        })
        .catch(function() {})
        .finally(function() {
          remaining--;
          if (remaining === 0) callback(products);
        });
    });
  }

  function buildSkeleton(count) {
    var html = '';
    for (var i = 0; i < count; i++) {
      html += '<div class="w-full"><div class="h-[350px] md:h-[450px] mb-4 w-full bg-muted animate-pulse"></div><div class="h-4 bg-muted animate-pulse w-3/4 mb-2"></div><div class="h-4 bg-muted animate-pulse w-1/4"></div></div>';
    }
    return html;
  }

  /* ══════════════════════════════════════════════
   * CAROUSEL
   * ══════════════════════════════════════════════ */

  function carousel(containerEl, opts) {
    opts = opts || {};
    var slideSelector = opts.slideSelector || ':scope > *';
    var loop = opts.loop !== false;
    var dragEnabled = opts.dragEnabled !== false;
    var dotsContainer = opts.dotsContainer || null;
    var arrowsContainer = opts.arrowsContainer || null;

    var track = containerEl.querySelector('[data-track]') || containerEl.firstElementChild;
    var slides = track.querySelectorAll(slideSelector);
    var totalSlides = slides.length;
    var currentIndex = 0;
    var isDragging = false;
    var startX = 0;
    var prevTranslate = 0;

    if (totalSlides === 0) return { destroy: function() {} };

    function getSlideWidth() {
      return slides[0].offsetWidth;
    }

    function goTo(index) {
      if (loop) {
        if (index < 0) index = totalSlides - 1;
        if (index >= totalSlides) index = 0;
      } else {
        index = Math.max(0, Math.min(totalSlides - 1, index));
      }
      currentIndex = index;
      var offset = -currentIndex * getSlideWidth();
      track.style.transition = 'transform 300ms ease';
      track.style.transform = 'translateX(' + offset + 'px)';
      prevTranslate = offset;
      updateDots();
      if (opts.onSlideChange) opts.onSlideChange(currentIndex);
    }

    function prev() { goTo(currentIndex - 1); }
    function next() { goTo(currentIndex + 1); }

    // Dots
    function updateDots() {
      if (!dotsContainer) return;
      var dots = dotsContainer.querySelectorAll('button');
      dots.forEach(function(d, i) {
        d.className = 'w-2.5 h-2.5 rounded-full transition-colors ' +
          (i === currentIndex ? 'bg-foreground' : 'bg-foreground/20');
      });
    }

    function renderDots(container) {
      dotsContainer = container || dotsContainer;
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      for (var i = 0; i < totalSlides; i++) {
        var dot = document.createElement('button');
        dot.className = 'w-2.5 h-2.5 rounded-full transition-colors ' +
          (i === 0 ? 'bg-foreground' : 'bg-foreground/20');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.dataset.idx = i;
        dot.addEventListener('click', function() { goTo(parseInt(this.dataset.idx)); });
        dotsContainer.appendChild(dot);
      }
    }

    // Drag
    if (dragEnabled) {
      containerEl.addEventListener('mousedown', onDragStart);
      containerEl.addEventListener('touchstart', onDragStart, { passive: true });
      containerEl.addEventListener('mousemove', onDragMove);
      containerEl.addEventListener('touchmove', onDragMove, { passive: true });
      containerEl.addEventListener('mouseup', onDragEnd);
      containerEl.addEventListener('mouseleave', onDragEnd);
      containerEl.addEventListener('touchend', onDragEnd);
    }

    function onDragStart(e) {
      isDragging = true;
      startX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      prevTranslate = -currentIndex * getSlideWidth();
      track.style.transition = 'none';
    }

    function onDragMove(e) {
      if (!isDragging) return;
      var x = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      track.style.transform = 'translateX(' + (prevTranslate + x - startX) + 'px)';
    }

    function onDragEnd(e) {
      if (!isDragging) return;
      isDragging = false;
      var endX = e.type.includes('mouse') ? e.pageX : (e.changedTouches ? e.changedTouches[0].clientX : startX);
      var moved = endX - startX;
      var threshold = getSlideWidth() * 0.2;
      if (moved < -threshold) next();
      else if (moved > threshold) prev();
      else goTo(currentIndex);
    }

    function destroy() {
      if (dragEnabled) {
        containerEl.removeEventListener('mousedown', onDragStart);
        containerEl.removeEventListener('touchstart', onDragStart);
        containerEl.removeEventListener('mousemove', onDragMove);
        containerEl.removeEventListener('touchmove', onDragMove);
        containerEl.removeEventListener('mouseup', onDragEnd);
        containerEl.removeEventListener('mouseleave', onDragEnd);
        containerEl.removeEventListener('touchend', onDragEnd);
      }
    }

    return {
      goTo: goTo,
      prev: prev,
      next: next,
      renderDots: renderDots,
      destroy: destroy,
      getCurrentIndex: function() { return currentIndex; }
    };
  }

  /* ══════════════════════════════════════════════
   * PUBLIC API
   * ══════════════════════════════════════════════ */

  return {
    flattenVariants: flattenVariants,
    renderCard: renderCard,
    collectionGrid: collectionGrid,
    fetchProducts: fetchAllProducts,
    Cart: Cart,
    carousel: carousel,
    toast: toast
  };

})();
